/**
 * Werkorder Detail/Print Pagina
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { werkordersService, Werkorder, WerkorderStatus, WerkorderTijdregistratie, WerkorderSectie, WerkorderTaak, WerkorderMateriaalverbruik } from '../../services/werkorders.service';
import { relationsService, Relation } from '../../services/relations.service';
import { offertesService, Offerte, OfferteCalculatie } from '../../services/offertes.service';
import { bedrijfsgegevensService, Bedrijfsgegevens } from '../../services/bedrijfsgegevens.service';
import { botenService, BootDetail } from '../../services/boten.service';
import { werknemersService, Werknemer } from '../../services/werknemers.service';
import { facturenService, FactuurListItem } from '../../services/facturen.service';
import { getApiBaseUrl } from '../../services/api';
import Toast from '../../components/Toast';
import './WerkorderDetail.css';

interface WerkorderDetailProps {
  werkorderId: number;
}

const WerkorderDetail = ({ werkorderId }: WerkorderDetailProps) => {
  const navigate = useNavigate();
  const [werkorder, setWerkorder] = useState<Werkorder | null>(null);
  const [klant, setKlant] = useState<Relation | null>(null);
  const [boot, setBoot] = useState<BootDetail | null>(null);
  const [offerte, setOfferte] = useState<Offerte | null>(null);
  const [bedrijf, setBedrijf] = useState<Bedrijfsgegevens | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [showUren, setShowUren] = useState(true);
  const [logoError, setLogoError] = useState(false);

  // Uren sectie state
  const [werknemers, setWerknemers] = useState<Werknemer[]>([]);
  const [tijdregistraties, setTijdregistraties] = useState<WerkorderTijdregistratie[]>([]);
  const [materiaalverbruik, setMateriaalverbruik] = useState<WerkorderMateriaalverbruik[]>([]);
  const [facturen, setFacturen] = useState<FactuurListItem[]>([]);
  const [facturenLoading, setFacturenLoading] = useState(false);
  const [geselecteerdeUren, setGeselecteerdeUren] = useState<Set<number>>(new Set());
  const [showMateriaalModal, setShowMateriaalModal] = useState(false);
  const [editMateriaal, setEditMateriaal] = useState<WerkorderMateriaalverbruik | null>(null);
  const [materiaalFormData, setMateriaalFormData] = useState({
    datum: new Date().toISOString().split('T')[0],
    aantal: 1,
    prijs_per_eenheid: 0,
    omschrijving: '',
  });
  const [showUrenModal, setShowUrenModal] = useState(false);
  const [editRegistratie, setEditRegistratie] = useState<WerkorderTijdregistratie | null>(null);
  const [urenFormData, setUrenFormData] = useState({
    werknemer_id: '',
    datum: new Date().toISOString().split('T')[0],
    starttijd: '08:00',
    eindtijd: '17:00',
    aantal_uren: 8,
    omschrijving: '',
    uurloon: 0,
  });
  const [urenSuccess, setUrenSuccess] = useState<string | null>(null);

  // Secties en taken state
  const [secties, setSecties] = useState<WerkorderSectie[]>([]);
  const [selectedSectie, setSelectedSectie] = useState<number | null>(null);
  const [selectedTaak, setSelectedTaak] = useState<number | null>(null);
  const [takenVoorSectie, setTakenVoorSectie] = useState<WerkorderTaak[]>([]);

  useEffect(() => {
    loadData();
    loadWerknemers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [werkorderId]);

  // Load werknemers voor dropdown
  const loadWerknemers = async () => {
    try {
      const data = await werknemersService.getAll({ actief: true });
      setWerknemers(data.items);
    } catch (err) {
      console.error('Fout bij laden werknemers:', err);
    }
  };

  // Load tijdregistraties voor deze werkorder
  const loadTijdregistraties = async () => {
    if (!werkorder) return;
    try {
      const data = await werkordersService.getTijdregistraties(werkorderId);
      setTijdregistraties(data);
    } catch (err) {
      console.error('Fout bij laden tijdregistraties:', err);
    }
  };

  // Load materiaalverbruik voor deze werkorder
  const loadMateriaalverbruik = async () => {
    if (!werkorder) return;
    try {
      const data = await werkordersService.getMateriaalverbruik(werkorderId);
      setMateriaalverbruik(data);
    } catch (err) {
      console.error('Fout bij laden materiaalverbruik:', err);
    }
  };

  // Load facturen voor deze werkorder
  const loadFacturen = async () => {
    try {
      setFacturenLoading(true);
      const data = await facturenService.getByWerkorderId(werkorderId);
      setFacturen(data);
    } catch (err) {
      console.error('Fout bij laden facturen:', err);
    } finally {
      setFacturenLoading(false);
    }
  };

  useEffect(() => {
    if (werkorder) {
      loadTijdregistraties();
      loadMateriaalverbruik();
      loadFacturen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [werkorder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [werkorderData, bedrijfData] = await Promise.all([
        werkordersService.getById(werkorderId),
        bedrijfsgegevensService.get()
      ]);

      setWerkorder(werkorderData);
      setBedrijf(bedrijfData);

      const klantData = await relationsService.getById(werkorderData.relatie_id);
      setKlant(klantData);

      // Haal boot op als er een boot_id is
      if (werkorderData.boot_id) {
        const bootData = await botenService.getById(werkorderData.boot_id);
        setBoot(bootData);
      }

      if (werkorderData.offerte_id) {
        const offerteData = await offertesService.getById(werkorderData.offerte_id);
        setOfferte(offerteData);
      }

      setError(null);
    } catch (err: any) {
      setError(err.message || 'Kon werkorder niet laden');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: WerkorderStatus) => {
    if (!werkorder) return;

    try {
      setStatusUpdating(true);
      await werkordersService.updateStatus(werkorder.id, newStatus);
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Kon status niet bijwerken');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Laad secties en taken voor deze werkorder
  const loadSecties = async () => {
    try {
      const taken = await werkordersService.getWerkorderTaken(werkorderId);
      setSecties(taken.secties);
    } catch (err) {
      console.error('Fout bij laden secties:', err);
      setSecties([]);
    }
  };

  // Handle sectie selectie
  const handleSectieChange = (sectieId: string) => {
    const id = sectieId ? parseInt(sectieId) : null;
    setSelectedSectie(id);
    setSelectedTaak(null);

    if (id) {
      const sectie = secties.find(s => s.id === id);
      setTakenVoorSectie(sectie?.taken || []);
    } else {
      setTakenVoorSectie([]);
    }
  };

  // Handle taak selectie - vul omschrijving in
  const handleTaakChange = (taakId: string) => {
    const id = taakId ? parseInt(taakId) : null;
    setSelectedTaak(id);

    if (id) {
      const taak = takenVoorSectie.find(t => t.id === id);
      if (taak) {
        setUrenFormData({
          ...urenFormData,
          omschrijving: taak.omschrijving
        });
      }
    }
  };

  // Uren modal functies
  const openUrenModal = async (registratie?: WerkorderTijdregistratie) => {
    // Laad secties als dat nog niet gebeurd is
    if (secties.length === 0) {
      await loadSecties();
    }

    // Reset sectie/taak selectie
    setSelectedSectie(null);
    setSelectedTaak(null);
    setTakenVoorSectie([]);

    if (registratie) {
      setEditRegistratie(registratie);
      setUrenFormData({
        werknemer_id: registratie.werknemer_id.toString(),
        datum: registratie.datum,
        starttijd: registratie.starttijd ? new Date(registratie.starttijd).toTimeString().slice(0, 5) : '08:00',
        eindtijd: registratie.eindtijd ? new Date(registratie.eindtijd).toTimeString().slice(0, 5) : '17:00',
        aantal_uren: registratie.aantal_uren,
        omschrijving: registratie.omschrijving || '',
        uurloon: registratie.uurloon || 0,
      });
    } else {
      setEditRegistratie(null);
      const werknemer = werknemers[0];
      setUrenFormData({
        werknemer_id: werknemer?.id.toString() || '',
        datum: new Date().toISOString().split('T')[0],
        starttijd: '08:00',
        eindtijd: '17:00',
        aantal_uren: 8,
        omschrijving: '',
        uurloon: werknemer?.bruto_uurloon || 0,
      });
    }
    setShowUrenModal(true);
  };

  const handleWerknemerChange = (werknemerId: string) => {
    const werknemer = werknemers.find(w => w.id === parseInt(werknemerId));
    setUrenFormData({
      ...urenFormData,
      werknemer_id: werknemerId,
      uurloon: werknemer?.bruto_uurloon || 0,
    });
  };

  const handleUrenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!urenFormData.werknemer_id) {
      setError('Selecteer een werknemer');
      return;
    }

    // Validatie: sectie en taak zijn verplicht als er secties beschikbaar zijn
    if (secties.length > 0 && !selectedSectie) {
      setError('Selecteer een sectie');
      return;
    }

    if (takenVoorSectie.length > 0 && !selectedTaak) {
      setError('Selecteer een taak');
      return;
    }

    try {
      const datum = new Date(urenFormData.datum);
      const [startH, startM] = urenFormData.starttijd.split(':').map(Number);
      const [eindH, eindM] = urenFormData.eindtijd.split(':').map(Number);

      const starttijd = new Date(datum);
      starttijd.setHours(startH, startM, 0, 0);

      const eindtijd = new Date(datum);
      eindtijd.setHours(eindH, eindM, 0, 0);

      const registratieData = {
        werknemer_id: parseInt(urenFormData.werknemer_id),
        datum: urenFormData.datum,
        starttijd: starttijd.toISOString(),
        eindtijd: eindtijd.toISOString(),
        aantal_uren: urenFormData.aantal_uren,
        omschrijving: urenFormData.omschrijving || undefined,
        uurloon: urenFormData.uurloon,
        totaal_kosten: urenFormData.aantal_uren * urenFormData.uurloon,
        goedgekeurd: false,
      };

      if (editRegistratie && editRegistratie.id) {
        await werkordersService.updateTijdregistratie(editRegistratie.id, registratieData);
        setUrenSuccess('Urenregistratie bijgewerkt');
      } else {
        await werkordersService.createTijdregistratie(werkorderId, registratieData as any);
        setUrenSuccess('Uren geregistreerd');
      }

      setShowUrenModal(false);
      loadTijdregistraties();
      setTimeout(() => setUrenSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    }
  };

  const handleUrenDelete = async () => {
    if (!editRegistratie || !editRegistratie.id || !window.confirm('Weet je zeker dat je deze registratie wilt verwijderen?')) {
      return;
    }

    try {
      await werkordersService.deleteTijdregistratie(editRegistratie.id);
      setUrenSuccess('Registratie verwijderd');
      setShowUrenModal(false);
      loadTijdregistraties();
      setTimeout(() => setUrenSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen');
    }
  };

  const formatDisplayDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getWerknemerNaam = (werknemerId: number): string => {
    const werknemer = werknemers.find(w => w.id === werknemerId);
    if (!werknemer) return 'Onbekend';
    return `${werknemer.voornaam} ${werknemer.tussenvoegsel ? werknemer.tussenvoegsel + ' ' : ''}${werknemer.achternaam}`;
  };

  const getTotaalUren = (): number => {
    return tijdregistraties.reduce((sum, r) => sum + (parseFloat(String(r.aantal_uren)) || 0), 0);
  };

  const getTotaalMateriaalKosten = (): number => {
    return materiaalverbruik.reduce((sum, m) => sum + (parseFloat(String(m.totaal_kosten)) || 0), 0);
  };

  // Functies voor uren selectie
  const toggleUrenSelectie = (registratieId: number) => {
    const nieuweSelectie = new Set(geselecteerdeUren);
    if (nieuweSelectie.has(registratieId)) {
      nieuweSelectie.delete(registratieId);
    } else {
      nieuweSelectie.add(registratieId);
    }
    setGeselecteerdeUren(nieuweSelectie);
  };

  const selecteerAlleUren = () => {
    const alleIds = new Set(tijdregistraties.map(reg => reg.id).filter(id => id !== undefined) as number[]);
    setGeselecteerdeUren(alleIds);
  };

  const deselecteerAlleUren = () => {
    setGeselecteerdeUren(new Set());
  };

  const getGeselecteerdeUrenTotaal = (): { uren: number; kosten: number } => {
    const geselecteerde = tijdregistraties.filter(reg => reg.id && geselecteerdeUren.has(reg.id));
    return {
      uren: geselecteerde.reduce((sum, reg) => sum + (parseFloat(String(reg.aantal_uren)) || 0), 0),
      kosten: geselecteerde.reduce((sum, reg) => sum + (parseFloat(String(reg.totaal_kosten)) || 0), 0)
    };
  };

  // Materiaal modal functies
  const openNewMateriaalModal = () => {
    setEditMateriaal(null);
    setMateriaalFormData({
      datum: new Date().toISOString().split('T')[0],
      aantal: 1,
      prijs_per_eenheid: 0,
      omschrijving: '',
    });
    setShowMateriaalModal(true);
  };

  const openMateriaalModal = (materiaal: WerkorderMateriaalverbruik) => {
    setEditMateriaal(materiaal);
    setMateriaalFormData({
      datum: materiaal.datum,
      aantal: parseFloat(String(materiaal.aantal)),
      prijs_per_eenheid: parseFloat(String(materiaal.prijs_per_eenheid)),
      omschrijving: materiaal.omschrijving || '',
    });
    setShowMateriaalModal(true);
  };

  const handleMateriaalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const totaalKosten = materiaalFormData.aantal * materiaalFormData.prijs_per_eenheid;

      if (editMateriaal && editMateriaal.id) {
        // Update bestaand materiaalverbruik
        await werkordersService.updateMateriaalverbruik(editMateriaal.id, {
          datum: materiaalFormData.datum,
          aantal: materiaalFormData.aantal,
          prijs_per_eenheid: materiaalFormData.prijs_per_eenheid,
          totaal_kosten: totaalKosten,
          omschrijving: materiaalFormData.omschrijving,
        });
        setUrenSuccess('Materiaalverbruik bijgewerkt');
      } else {
        // Nieuw materiaalverbruik toevoegen
        await werkordersService.createMateriaalverbruik(werkorderId, {
          datum: materiaalFormData.datum,
          aantal: materiaalFormData.aantal,
          prijs_per_eenheid: materiaalFormData.prijs_per_eenheid,
          totaal_kosten: totaalKosten,
          omschrijving: materiaalFormData.omschrijving,
          eenheid: 'stuks',
        } as WerkorderMateriaalverbruik);
        setUrenSuccess('Materiaalverbruik toegevoegd');
      }

      setShowMateriaalModal(false);
      loadMateriaalverbruik();
      setTimeout(() => setUrenSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij opslaan');
    }
  };

  const handleMateriaalDelete = async () => {
    if (!editMateriaal || !editMateriaal.id || !window.confirm('Weet je zeker dat je dit materiaalverbruik wilt verwijderen? De voorraad wordt teruggezet.')) {
      return;
    }

    try {
      await werkordersService.deleteMateriaalverbruik(editMateriaal.id);
      setUrenSuccess('Materiaalverbruik verwijderd');
      setShowMateriaalModal(false);
      loadMateriaalverbruik();
      setTimeout(() => setUrenSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Fout bij verwijderen');
    }
  };

  const getKlantNaamKort = () => {
    if (!klant) return '';
    let naam = '';

    if (klant.bedrijfsnaam === 'Particulier') {
      // Format: Dhr. Achternaam, V. van
      const aanhef = klant.aanhef || '';
      const achternaam = klant.achternaam || '';
      const voorletter = klant.voornaam ? `${klant.voornaam.charAt(0)}.` : '';
      const tussenvoegsel = klant.tussenvoegsel || '';

      if (tussenvoegsel) {
        naam = `${aanhef} ${achternaam}, ${voorletter} ${tussenvoegsel}`;
      } else {
        naam = `${aanhef} ${achternaam}, ${voorletter}`;
      }
    } else {
      naam = klant.bedrijfsnaam;
    }

    // Voeg bootnaam toe als die er is
    if (boot?.bootnaam) {
      naam += ` (${boot.bootnaam})`;
    }

    return naam;
  };

  if (loading) {
    return (
      <div className="werkorder-detail-container">
        <div className="loading">Werkorder laden...</div>
      </div>
    );
  }

  if (error || !werkorder || !bedrijf) {
    return (
      <div className="werkorder-detail-container">
        <div className="error-message">{error || 'Werkorder niet gevonden'}</div>
        <button className="btn btn-secondary" onClick={() => navigate('/werkorders')}>
          Terug naar Werkorders
        </button>
      </div>
    );
  }

  const logoUrl = `${getApiBaseUrl()}/api/v1/files/logo`;

  // Render company header (logo + bedrijfsgegevens)
  const renderCompanyHeader = () => (
    <div className="werkorder-header">
      <div className="header-left">
        {!logoError ? (
          <img
            src={logoUrl}
            alt={bedrijf.bedrijfsnaam || 'Logo'}
            className="company-logo"
            onError={() => setLogoError(true)}
          />
        ) : (
          <div className="company-name-large">
            <span className="buro">Buro</span>
            <span className="vier">42</span>
          </div>
        )}
      </div>
      <div className="header-right">
        <div className="company-details">
          <strong>{bedrijf.bedrijfsnaam}</strong>
          {bedrijf.straat && bedrijf.huisnummer && (
            <div>{bedrijf.straat} {bedrijf.huisnummer}</div>
          )}
          {bedrijf.postcode && bedrijf.plaats && (
            <div>{bedrijf.postcode} {bedrijf.plaats}</div>
          )}
          {bedrijf.telefoon && <div>Tel. {bedrijf.telefoon}</div>}
        </div>
      </div>
    </div>
  );

  // Render titel regel (klant + onderwerp)
  const renderTitelRegel = () => (
    <div className="werkorder-titel-regel">
      <span className="klant-naam">{getKlantNaamKort()}</span>
      <span className="werkorder-onderwerp">{werkorder.onderwerp}</span>
    </div>
  );

  // Render calculatie sectie content
  const renderCalculatieContent = (calculatie: OfferteCalculatie) => {
    const arbeidRegels = calculatie.regels.filter((r: any) => r.type_regel === 'ARBEID');
    const materiaalRegels = calculatie.regels.filter((r: any) =>
      r.type_regel === 'MATERIAAL' || r.type_regel === 'ONDERDEEL' || r.type_regel === 'UITBESTEED' || r.type_regel === 'TEKST'
    );

    return (
      <div className="werkorder-section">
        <h2 className="sectie-titel">Sectie: {calculatie.naam}</h2>
        {calculatie.omschrijving && (
          <p className="sectie-omschrijving">{calculatie.omschrijving}</p>
        )}

        {/* Werkzaamheden */}
        {arbeidRegels.length > 0 && (
          <div className="regels-groep">
            <h3>Werkzaamheden</h3>
            <div className="regels-lijst">
              {arbeidRegels.map((regel: any, idx: number) => (
                <div key={idx} className="regel-item">
                  <span className="regel-checkbox">☐</span>
                  <span className="regel-omschrijving">{regel.omschrijving}</span>
                  {showUren && <span className="regel-uren">{regel.aantal} {regel.eenheid}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Materialen */}
        {materiaalRegels.length > 0 && (
          <div className="regels-groep">
            <h3>Materialen</h3>
            <div className="regels-lijst">
              {materiaalRegels.map((regel: any, idx: number) => (
                <div key={idx} className="regel-item">
                  <span className="regel-checkbox">☐</span>
                  <span className="regel-omschrijving">{regel.omschrijving}</span>
                  {regel.type_regel !== 'TEKST' && (
                    <span className="regel-aantal">{regel.aantal} {regel.eenheid}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render handtekening en notities sectie
  const renderFooterSections = () => (
    <>
      {/* Notities sectie */}
      <div className="werkorder-section notities-sectie">
        <h2 className="sectie-titel">Notities</h2>
        <div className="notities-ruimte"></div>
      </div>

      {/* Handtekening sectie */}
      <div className="handtekening-sectie">
        <div className="handtekening-blok">
          <div className="handtekening-label">Uitgevoerd door:</div>
          <div className="handtekening-lijn"></div>
          <div className="handtekening-datum">Datum: ________________</div>
        </div>
        <div className="handtekening-blok">
          <div className="handtekening-label">Akkoord klant:</div>
          <div className="handtekening-lijn"></div>
          <div className="handtekening-datum">Datum: ________________</div>
        </div>
      </div>
    </>
  );

  // Render alle pagina's
  const renderPages = () => {
    const pages = [];
    const calculaties = offerte?.calculaties || [];

    if (calculaties.length === 0) {
      // Geen calculaties: alles op 1 pagina
      pages.push(
        <div key="page-1" className="werkorder-document">
          {renderCompanyHeader()}
          {renderTitelRegel()}
          {werkorder.omschrijving && (
            <div className="werkorder-omschrijving">
              {werkorder.omschrijving}
            </div>
          )}
          {renderFallbackRegels()}
          <div className="footer-wrapper">
            {renderFooterSections()}
          </div>
        </div>
      );
    } else {
      // Meerdere calculaties: elke calculatie op eigen pagina
      calculaties.forEach((calculatie, index) => {
        const isFirstPage = index === 0;
        const isLastPage = index === calculaties.length - 1;

        pages.push(
          <div key={`page-${index + 1}`} className="werkorder-document">
            {renderCompanyHeader()}
            {isFirstPage ? (
              <>
                {renderTitelRegel()}
                {werkorder.omschrijving && (
                  <div className="werkorder-omschrijving">
                    {werkorder.omschrijving}
                  </div>
                )}
              </>
            ) : (
              <div className="werkorder-info-small">
                <span className="klant-naam">{getKlantNaamKort()}</span>
                <span className="pagina-info">Pagina {index + 1} van {calculaties.length}</span>
              </div>
            )}
            {renderCalculatieContent(calculatie)}
            {isLastPage && (
              <div className="footer-wrapper">
                {renderFooterSections()}
              </div>
            )}
          </div>
        );
      });
    }

    return pages;
  };

  // Fallback regels (zonder calculaties)
  const renderFallbackRegels = () => {
    const arbeidRegels = werkorder.regels.filter(r => r.type_regel === 'ARBEID');
    const materiaalRegels = werkorder.regels.filter(r =>
      r.type_regel === 'MATERIAAL' || r.type_regel === 'ONDERDEEL' || r.type_regel === 'UITBESTEED'
    );

    return (
      <>
        {arbeidRegels.length > 0 && (
          <div className="werkorder-section">
            <h2 className="sectie-titel">Werkzaamheden</h2>
            <div className="regels-lijst">
              {arbeidRegels.map((regel, idx) => (
                <div key={idx} className="regel-item">
                  <span className="regel-checkbox">☐</span>
                  <span className="regel-omschrijving">{regel.omschrijving}</span>
                  {showUren && <span className="regel-uren">{regel.aantal_gepland} {regel.eenheid}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {materiaalRegels.length > 0 && (
          <div className="werkorder-section">
            <h2 className="sectie-titel">Materialen</h2>
            <div className="regels-lijst">
              {materiaalRegels.map((regel, idx) => (
                <div key={idx} className="regel-item">
                  <span className="regel-checkbox">☐</span>
                  <span className="regel-omschrijving">{regel.omschrijving}</span>
                  <span className="regel-aantal">{regel.aantal_gepland} {regel.eenheid}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="werkorder-detail-container">
      {/* Actie knoppen (niet printen) */}
      <div className="print-hide action-buttons">
        <button className="btn btn-secondary" onClick={() => navigate('/werkorders')}>
          Terug
        </button>
        <button className="btn btn-primary" onClick={handlePrint}>
          Afdrukken
        </button>
        <label className="uren-toggle">
          <input
            type="checkbox"
            checked={showUren}
            onChange={(e) => setShowUren(e.target.checked)}
          />
          Uren tonen
        </label>
        {werkorder.offerte_id && (
          <button className="btn btn-info" onClick={() => navigate(`/offertes/${werkorder.offerte_id}`)}>
            Offerte
          </button>
        )}
        <div className="status-control">
          <label>Status:</label>
          <select
            value={werkorder.status}
            onChange={(e) => handleStatusChange(e.target.value as WerkorderStatus)}
            disabled={statusUpdating}
            style={{ backgroundColor: werkordersService.getStatusColor(werkorder.status), color: 'white' }}
          >
            <option value="NIEUW">Nieuw</option>
            <option value="GEPLAND">Gepland</option>
            <option value="IN_UITVOERING">In uitvoering</option>
            <option value="OPGESCHORT">Opgeschort</option>
            <option value="VOLTOOID">Voltooid</option>
            <option value="GEANNULEERD">Geannuleerd</option>
          </select>
        </div>
      </div>

      {/* Render pagina's */}
      {renderPages()}

      {/* Uren sectie (niet printen) */}
      <div className="print-hide uren-sectie">
        <div className="uren-header">
          <h2>Geregistreerde Uren</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {geselecteerdeUren.size > 0 && (
              <button 
                className="btn btn-secondary"
                onClick={deselecteerAlleUren}
              >
                Selectie Wissen
              </button>
            )}
            <button className="btn btn-primary" onClick={() => openUrenModal()}>
              + Uren Toevoegen
            </button>
          </div>
        </div>

        <Toast message={urenSuccess} type="success" onClose={() => setUrenSuccess(null)} />

        {tijdregistraties.length > 0 ? (
          <>
            <div className="uren-summary" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <div className="summary-item">
                <span className="label">Totaal uren:</span>
                <span className="value">{getTotaalUren().toFixed(1)} uur</span>
              </div>
              <div className="summary-item">
                <span className="label">Registraties:</span>
                <span className="value">{tijdregistraties.length}</span>
              </div>
              {geselecteerdeUren.size > 0 && (
                <>
                  <div className="summary-item" style={{
                    backgroundColor: '#e0f2fe',
                    color: '#0277bd',
                    padding: '0.5rem 0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #81d4fa'
                  }}>
                    <span className="label">Geselecteerd:</span>
                    <span className="value" style={{ fontWeight: 600 }}>
                      {getGeselecteerdeUrenTotaal().uren.toFixed(1)} uur (€ {getGeselecteerdeUrenTotaal().kosten.toFixed(2)})
                    </span>
                  </div>
                  <button
                    className="btn"
                    style={{ backgroundColor: '#16a34a', color: 'white', border: 'none' }}
                    disabled={facturenLoading}
                    onClick={async () => {
                      try {
                        setFacturenLoading(true);
                        await facturenService.createFromSelectedTimeRegistrations({
                          werkorder_id: werkorderId,
                          betalingstermijn: 30,
                          tijdregistratie_ids: Array.from(geselecteerdeUren)
                        });
                        await loadFacturen();
                        setGeselecteerdeUren(new Set());
                        setUrenSuccess('Factuur aangemaakt met geselecteerde uren');
                        setTimeout(() => setUrenSuccess(null), 3000);
                      } catch (err: any) {
                        setError(err.message || 'Kon factuur niet aanmaken');
                      } finally {
                        setFacturenLoading(false);
                      }
                    }}
                  >
                    {facturenLoading ? 'Bezig...' : `Factuur Aanmaken (${geselecteerdeUren.size} uren)`}
                  </button>
                </>
              )}
            </div>
            

            <table className="uren-tabel">
              <thead>
                <tr>
                  <th style={{ width: '50px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={geselecteerdeUren.size === tijdregistraties.length && tijdregistraties.length > 0}
                      onChange={(e) => e.target.checked ? selecteerAlleUren() : deselecteerAlleUren()}
                      title="Alles selecteren"
                      style={{ transform: 'scale(1.1)' }}
                    />
                  </th>
                  <th>Datum</th>
                  <th>Werknemer</th>
                  <th>Omschrijving</th>
                  <th>Uren</th>
                  <th>Prijs/uur</th>
                  <th>Totaal</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {tijdregistraties.map(reg => (
                  <tr key={reg.id} className={reg.goedgekeurd ? 'goedgekeurd' : ''}>
                    <td style={{ textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={reg.id ? geselecteerdeUren.has(reg.id) : false}
                        onChange={() => reg.id && toggleUrenSelectie(reg.id)}
                        title="Selecteer voor facturering"
                        style={{ transform: 'scale(1.1)' }}
                      />
                    </td>
                    <td>{formatDisplayDate(reg.datum)}</td>
                    <td>{getWerknemerNaam(reg.werknemer_id)}</td>
                    <td className="omschrijving-cel">{reg.omschrijving || '-'}</td>
                    <td className="uren-cel">{parseFloat(String(reg.aantal_uren) || '0').toFixed(1)}</td>
                    <td>€ {parseFloat(String(reg.uurloon) || '0').toFixed(2)}</td>
                    <td className="uren-cel">€ {parseFloat(String(reg.totaal_kosten) || '0').toFixed(2)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openUrenModal(reg)}
                      >
                        Bewerken
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="geen-uren">
            Nog geen uren geregistreerd voor deze werkorder.
          </div>
        )}
      </div>

      {/* Materiaalverbruik sectie (niet printen) */}
      <div className="print-hide materiaal-sectie">
        <div className="uren-header">
          <h2>Materiaalverbruik</h2>
          <button className="btn btn-primary" onClick={() => openNewMateriaalModal()}>
            + Materiaal Toevoegen
          </button>
        </div>

        {materiaalverbruik.length > 0 ? (
          <>
            <div className="uren-summary">
              <div className="summary-item">
                <span className="label">Totaal kosten:</span>
                <span className="value">€ {getTotaalMateriaalKosten().toFixed(2)}</span>
              </div>
              <div className="summary-item">
                <span className="label">Artikelen:</span>
                <span className="value">{materiaalverbruik.length}</span>
              </div>
            </div>

            <table className="uren-tabel">
              <thead>
                <tr>
                  <th>Datum</th>
                  <th>Werknemer</th>
                  <th>Omschrijving</th>
                  <th>Aantal</th>
                  <th>Prijs/stuk</th>
                  <th>Totaal</th>
                  <th>Acties</th>
                </tr>
              </thead>
              <tbody>
                {materiaalverbruik.map(mat => (
                  <tr key={mat.id}>
                    <td>{formatDisplayDate(mat.datum)}</td>
                    <td>{mat.geregistreerd_door_werknemer_id ? getWerknemerNaam(mat.geregistreerd_door_werknemer_id) : '-'}</td>
                    <td>{mat.omschrijving || '-'}</td>
                    <td>{parseFloat(String(mat.aantal))} {mat.eenheid || 'stuks'}</td>
                    <td>€ {parseFloat(String(mat.prijs_per_eenheid)).toFixed(2)}</td>
                    <td className="uren-cel">€ {parseFloat(String(mat.totaal_kosten)).toFixed(2)}</td>
                    <td>
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => openMateriaalModal(mat)}
                      >
                        Bewerken
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="geen-uren">
            Nog geen materiaalverbruik geregistreerd voor deze werkorder.
          </div>
        )}
      </div>

      {/* Facturen sectie (niet printen) */}
      <div className="print-hide factuur-sectie" style={{ marginTop: '2rem', background: 'white', borderRadius: '12px', padding: '1.5rem', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
        <div className="uren-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h2 style={{ margin: 0 }}>Facturen</h2>
          <button
            className="btn btn-primary"
            onClick={async () => {
              if (geselecteerdeUren.size === 0) {
                setError('Selecteer eerst tijdregistraties om te factureren');
                return;
              }

              try {
                setFacturenLoading(true);

                await facturenService.createFromSelectedTimeRegistrations({
                  werkorder_id: werkorderId,
                  betalingstermijn: 30,
                  tijdregistratie_ids: Array.from(geselecteerdeUren)
                });

                await loadFacturen();
                setGeselecteerdeUren(new Set());
                setUrenSuccess('Factuur aangemaakt met geselecteerde uren');
                setTimeout(() => setUrenSuccess(null), 3000);
              } catch (err: any) {
                setError(err.message || 'Kon factuur niet aanmaken');
              } finally {
                setFacturenLoading(false);
              }
            }}
            disabled={facturenLoading || geselecteerdeUren.size === 0}
            title={geselecteerdeUren.size === 0 ? 'Selecteer eerst tijdregistraties' : `Factuur aanmaken met ${geselecteerdeUren.size} geselecteerde registraties`}
          >
            {facturenLoading
              ? 'Bezig...'
              : geselecteerdeUren.size > 0
                ? `+ Factuur Aanmaken (${geselecteerdeUren.size} uren)`
                : '+ Factuur Aanmaken'}
          </button>
        </div>

        {facturenLoading ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b' }}>Laden...</div>
        ) : facturen.length > 0 ? (
          <table className="uren-tabel">
            <thead>
              <tr>
                <th>Factuurnummer</th>
                <th>Datum</th>
                <th>Vervaldatum</th>
                <th>Status</th>
                <th>Totaal</th>
                <th>Openstaand</th>
              </tr>
            </thead>
            <tbody>
              {facturen.map(factuur => (
                <tr key={factuur.id}>
                  <td>{factuur.factuurnummer}</td>
                  <td>{formatDisplayDate(factuur.factuur_datum)}</td>
                  <td>{formatDisplayDate(factuur.vervaldatum)}</td>
                  <td>
                    <span className={`status-badge status-${factuur.status.toLowerCase()}`} style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: 500,
                      backgroundColor:
                        factuur.status === 'CONCEPT' ? '#f3f4f6' :
                        factuur.status === 'VERSTUURD' ? '#dbeafe' :
                        factuur.status === 'BETAALD' ? '#d1fae5' :
                        factuur.status === 'DEELS_BETAALD' ? '#fef3c7' :
                        factuur.status === 'VERVALLEN' ? '#fee2e2' :
                        factuur.status === 'GEANNULEERD' ? '#f3f4f6' : '#f3f4f6',
                      color:
                        factuur.status === 'CONCEPT' ? '#4b5563' :
                        factuur.status === 'VERSTUURD' ? '#1d4ed8' :
                        factuur.status === 'BETAALD' ? '#059669' :
                        factuur.status === 'DEELS_BETAALD' ? '#d97706' :
                        factuur.status === 'VERVALLEN' ? '#dc2626' :
                        factuur.status === 'GEANNULEERD' ? '#6b7280' : '#4b5563'
                    }}>
                      {factuur.status === 'CONCEPT' ? 'Concept' :
                       factuur.status === 'VERSTUURD' ? 'Verstuurd' :
                       factuur.status === 'BETAALD' ? 'Betaald' :
                       factuur.status === 'DEELS_BETAALD' ? 'Deels betaald' :
                       factuur.status === 'VERVALLEN' ? 'Vervallen' :
                       factuur.status === 'GEANNULEERD' ? 'Geannuleerd' : factuur.status}
                    </span>
                  </td>
                  <td>€ {parseFloat(String(factuur.totaal)).toFixed(2)}</td>
                  <td style={{ color: parseFloat(String(factuur.bedrag_openstaand)) > 0 ? '#dc2626' : '#059669' }}>
                    € {parseFloat(String(factuur.bedrag_openstaand)).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="geen-uren">
            Nog geen factuur aangemaakt voor deze werkorder.
          </div>
        )}
      </div>

      {/* Uren Modal */}
      {showUrenModal && (
        <div className="modal-overlay" onClick={() => setShowUrenModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editRegistratie ? 'Uren bewerken' : 'Uren registreren'}</h2>
              <button className="modal-close" onClick={() => setShowUrenModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleUrenSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Werknemer *</label>
                  <select
                    value={urenFormData.werknemer_id}
                    onChange={(e) => handleWerknemerChange(e.target.value)}
                    required
                  >
                    <option value="">Selecteer werknemer</option>
                    {werknemers.map(w => (
                      <option key={w.id} value={w.id}>
                        {w.voornaam} {w.tussenvoegsel ? w.tussenvoegsel + ' ' : ''}{w.achternaam}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sectie dropdown - altijd tonen als er secties zijn */}
                {secties.length > 0 && (
                  <div className="form-group">
                    <label>Sectie *</label>
                    <select
                      value={selectedSectie || ''}
                      onChange={(e) => handleSectieChange(e.target.value)}
                      required
                    >
                      <option value="">Selecteer sectie</option>
                      {secties.map(sectie => (
                        <option key={sectie.id} value={sectie.id}>
                          {sectie.naam}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Taak dropdown - alleen tonen als er taken zijn voor de geselecteerde sectie */}
                {takenVoorSectie.length > 0 && (
                  <div className="form-group">
                    <label>Taak *</label>
                    <select
                      value={selectedTaak || ''}
                      onChange={(e) => handleTaakChange(e.target.value)}
                      required
                    >
                      <option value="">Selecteer taak</option>
                      {takenVoorSectie.map(taak => (
                        <option key={taak.id} value={taak.id}>
                          {taak.omschrijving} ({taak.aantal} {taak.eenheid})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={urenFormData.datum}
                    onChange={(e) => setUrenFormData({ ...urenFormData, datum: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Aantal uren</label>
                  <input
                    type="number"
                    step="0.25"
                    min="0"
                    max="24"
                    value={urenFormData.aantal_uren}
                    onChange={(e) => setUrenFormData({ ...urenFormData, aantal_uren: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Omschrijving werkzaamheden</label>
                  <textarea
                    value={urenFormData.omschrijving}
                    onChange={(e) => setUrenFormData({ ...urenFormData, omschrijving: e.target.value })}
                    placeholder="Wat heb je gedaan?"
                  />
                </div>
              </div>

              <div className="modal-footer">
                {editRegistratie && (
                  <button type="button" className="btn btn-danger" onClick={handleUrenDelete}>
                    Verwijderen
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowUrenModal(false)}>
                  Annuleren
                </button>
                <button type="submit" className="btn btn-primary">
                  {editRegistratie ? 'Opslaan' : 'Registreren'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Materiaal Modal */}
      {showMateriaalModal && (
        <div className="modal-overlay" onClick={() => setShowMateriaalModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editMateriaal ? 'Materiaalverbruik bewerken' : 'Materiaal toevoegen'}</h2>
              <button className="modal-close" onClick={() => setShowMateriaalModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleMateriaalSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Omschrijving</label>
                  <input
                    type="text"
                    value={materiaalFormData.omschrijving}
                    onChange={(e) => setMateriaalFormData({ ...materiaalFormData, omschrijving: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Datum</label>
                  <input
                    type="date"
                    value={materiaalFormData.datum}
                    onChange={(e) => setMateriaalFormData({ ...materiaalFormData, datum: e.target.value })}
                    required
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Aantal</label>
                    <input
                      type="number"
                      step="1"
                      min="1"
                      value={materiaalFormData.aantal}
                      onChange={(e) => setMateriaalFormData({ ...materiaalFormData, aantal: parseFloat(e.target.value) || 1 })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Prijs/stuk (€)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={materiaalFormData.prijs_per_eenheid}
                      onChange={(e) => setMateriaalFormData({ ...materiaalFormData, prijs_per_eenheid: parseFloat(e.target.value) || 0 })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Totaal</label>
                  <div style={{ padding: '0.5rem 0.75rem', background: '#f8f9fa', borderRadius: '6px', fontWeight: 600 }}>
                    € {(materiaalFormData.aantal * materiaalFormData.prijs_per_eenheid).toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                {editMateriaal && (
                  <button type="button" className="btn btn-danger" onClick={handleMateriaalDelete}>
                    Verwijderen
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={() => setShowMateriaalModal(false)}>
                  Annuleren
                </button>
                <button type="submit" className="btn btn-primary">
                  {editMateriaal ? 'Opslaan' : 'Toevoegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default WerkorderDetail;
